import { ReadonlyVec3 } from 'gl-matrix';
import { RoiId, StructureSetId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DicomColor } from '../dicom/dicomColor';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { ContourEditBlocker } from '../dicom/contourEditBlocker';
import {
    RoiGenerationAlgorithmType,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { StructureSet } from '../patient/structureset';
import type { RecordedDatasetOrigin } from '../provenance/RecordedDatasetOrigin';
import type { RecordedRoiReview } from '../provenance/RecordedRoiReview';

/**
 * Immutable ROI read model for React / Recoil consumption.
 */
export interface RoiReadModel {
    readonly roiId: RoiId;
    /**
     * The ROI Number the DICOM file holds the structure under. Shown to the
     * user only, never used to find a structure: a later save can give the
     * number to a different one.
     */
    readonly roiNumberInFile: number;
    /** DICOM UID of the structure set that owns this ROI. */
    readonly structureSetUid: string;
    readonly name: string;
    readonly color: Readonly<DicomColor>;
    readonly visible: boolean;
    /** User edit lock: saved with the structure set, never exported to DICOM. */
    readonly locked: boolean;
    readonly contourThickness: number;
    readonly approvalStatus: DICOMApprovalStatus;
    readonly interpretedType: RoiInterpretedType;
    readonly electronDensityOverride: number | undefined;
    readonly massDensityOverride: number | undefined;
    readonly roiDescription: string;
    readonly roiGenerationAlgorithm: RoiGenerationAlgorithmType;
    /** Produced by the AI model whose result the set is (connector record, not the DICOM tag). */
    readonly modelGenerated: boolean;
    /** A model-generated structure whose contour has been changed since generation. */
    readonly contourEditedSinceGeneration: boolean;
    /** The structure's own review as the connector recorded it; null when none is, or it changed since. */
    readonly recordedReview: RecordedRoiReview | null;
    readonly contourCount: number;
    readonly isApproved: boolean;
    /**
     * Whether contour geometry may be written: not approved, parent set not
     * approved, not locked. Mirrors `DicomRoi.canModifyContours` so the UI
     * reads the domain's rule instead of re-deriving it per call site.
     */
    readonly canModifyContours: boolean;
    /**
     * Which guard refuses a shape change, for wording the refusal. Mirrors
     * `DicomRoi.contourEditBlocker`, parent-set approval included.
     */
    readonly contourEditBlocker: ContourEditBlocker | undefined;
    readonly isPointRoi: boolean;
    readonly identificationCode: RoiIdentificationCodeSequence | undefined;
    /** Single-point coordinate for POINT contours (markers, isocenters, etc.) in mm */
    readonly pointCoordinatesMm: ReadonlyVec3 | null;
}

/**
 * Immutable StructureSet read model for React / Recoil consumption.
 */
export interface StructureSetReadModel {
    /** Branded domain identifier — pass to services / useLiveStructureSet. */
    readonly id: StructureSetId;
    readonly dicomUid: string;
    readonly structureSetLabel: string;
    readonly structureSetDescription: string;
    readonly approvalStatus: DICOMApprovalStatus;
    readonly isApproved: boolean;
    readonly reviewerName: string;
    readonly reviewDate: string;
    readonly reviewTime: string;
    readonly frameOfReferenceUid: string;
    readonly numberOfStructures: number;
    readonly roiArray: ReadonlyArray<RoiReadModel>;
    readonly manufacturer: string;
    readonly manufacturerModelName: string;
    readonly softwareVersions: string;
    readonly hasImage: boolean;
    readonly imageUid: string | null;
    readonly generationModel?: GenerationModelDetails;
    /** The connector's record of how the set came to exist; null when none. */
    readonly recordedOrigin: RecordedDatasetOrigin | null;
    /** True for structure sets created in this session (not yet persisted). */
    readonly isNew: boolean;
    /** True once the structure set has been exported / uploaded. */
    readonly isExported: boolean;
}

/**
 * ROIs in `roiArray` carrying `interpretedType`, optionally excluding one
 * ROI. Returns every match: unlike EXTERNAL, some interpreted types
 * (SUPPORT in particular) are legitimately multi-valued per set, so a
 * caller wanting "is there one" or "the first" must say so itself. For
 * EXTERNAL use {@link findExternalRoiReadModel}, which encodes the
 * singleton. Read-model only — the domain has no generic counterpart.
 */
export function findRoisByInterpretedType(
    roiArray: readonly RoiReadModel[],
    interpretedType: RoiInterpretedType,
    excludeRoiId?: RoiId
): RoiReadModel[] {
    return roiArray.filter(
        (r) => r.roiId !== excludeRoiId && r.interpretedType === interpretedType
    );
}

/**
 * Builds an immutable {@link StructureSetReadModel} from a live
 * {@link StructureSet} domain object.
 */
export function createStructureSetReadModel(
    structureSet: StructureSet
): StructureSetReadModel {
    const domainRoiById = new Map(
        structureSet.rois.map((roi) => [roi.id, roi])
    );
    const roiReadModels: RoiReadModel[] = structureSet.roiArray.map((roi) =>
        Object.freeze({
            roiId: roi.uid,
            roiNumberInFile: roi.roiNumberInFile,
            structureSetUid: structureSet.dicomUid,
            name: roi.name,
            roiDescription: roi.roiDescription,
            color: Object.freeze({ ...roi.color }),
            visible: roi.visible,
            locked: roi.locked,
            contourThickness: roi.contourThickness,
            approvalStatus: roi.approvalStatus,
            interpretedType: roi.interpretedType,
            electronDensityOverride: roi.electronDensityOverride,
            massDensityOverride: roi.massDensityOverride,
            roiGenerationAlgorithm: roi.roiGenerationAlgorithm,
            modelGenerated: domainRoiById.get(roi.uid)?.modelGenerated ?? false,
            contourEditedSinceGeneration:
                domainRoiById.get(roi.uid)?.contourEditedSinceGeneration ??
                false,
            recordedReview: domainRoiById.get(roi.uid)?.recordedReview ?? null,
            contourCount: roi.contours.length,
            isApproved: roi.isApproved,
            canModifyContours: roi.canModifyContours,
            contourEditBlocker: roi.contourEditBlocker,
            isPointRoi: roi.isPointRoi,
            identificationCode: roi.identificationCode,
            pointCoordinatesMm: roi.pointCoordinatesMm,
        })
    );

    return Object.freeze({
        id: structureSet.id,
        dicomUid: structureSet.dicomUid,
        structureSetLabel: structureSet.structureSetLabel,
        structureSetDescription: structureSet.structureSetDescription,
        approvalStatus: structureSet.approvalStatus,
        isApproved: structureSet.isApproved,
        reviewerName: structureSet.reviewerName,
        reviewDate: structureSet.reviewDate,
        reviewTime: structureSet.reviewTime,
        frameOfReferenceUid: structureSet.frameOfReferenceUid,
        numberOfStructures: structureSet.numberOfStructures,
        roiArray: Object.freeze(roiReadModels),
        manufacturer: structureSet.manufacturer,
        manufacturerModelName: structureSet.manufacturerModelName,
        softwareVersions: structureSet.softwareVersions,
        hasImage: structureSet.image !== null,
        imageUid: structureSet.image?.dicomUid ?? null,
        generationModel: structureSet.generationModel,
        recordedOrigin: structureSet.recordedOrigin,
        isNew: structureSet.isNew,
        isExported: structureSet.isExported,
    });
}

/**
 * The EXTERNAL (body contour) ROI of a projected structure set, or
 * undefined. The read-model counterpart of
 * {@link DicomStructureSet.findExternalRoi}, with the same
 * `excludeRoiId` escape so a retype of the EXTERNAL itself is legal.
 *
 * The single-EXTERNAL invariant is enforced in the domain
 * (`DuplicateExternalRoiError`); this only mirrors it so a UI can refuse
 * the choice with a reason instead of surfacing a thrown error.
 */
export function findExternalRoiReadModel(
    structureSet: Pick<StructureSetReadModel, 'roiArray'> | null | undefined,
    excludeRoiId?: RoiId
): RoiReadModel | undefined {
    return structureSet?.roiArray.find(
        (roi) =>
            roi.roiId !== excludeRoiId &&
            roi.interpretedType === RoiInterpretedType.EXTERNAL
    );
}
